import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {ActivatedRoute, ParamMap, Router, RouterLink} from '@angular/router';
import {combineLatest, defaultIfEmpty, distinctUntilChanged, map, Observable, Subject, of} from 'rxjs';
import {debounceTime, switchMap, takeUntil, tap} from 'rxjs/operators';
import {ObjectService} from "../../../services/objects/abstractions/objects/object.service.abstract";
import {ObjectServiceFactory} from "../../../services/objects/abstractions/objects/object-service.factory";
import {AsyncPipe, DatePipe, SlicePipe, TitleCasePipe} from "@angular/common";
import {MaterialModule} from "../../../material.module";
import {TablerIconComponent} from "../../../shared/tabler-icons/tabler-icons";
import {SelectionModel} from "@angular/cdk/collections";
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {CreateObjectDialogComponent} from './create-object-dialog/create-object-dialog.component'; // Adjust the path as needed
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {ClientService} from "../../../services/auth/client.service";
import {ClientStateService} from '../../../services/auth/client-state.service';
import {ToastService} from "../../../services/layout/toast.service";
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatTableDataSource} from '@angular/material/table';
import {FunctionsService} from '../../../services/objects/functions.service';

@Component({
  selector: 'app-listings',
  templateUrl: './listings.component.html',
  styleUrls: ['./listings.component.scss'],
  imports: [
    MaterialModule,
    DatePipe,
    TitleCasePipe,
    TablerIconComponent,
    SlicePipe,
    RouterLink,
    AsyncPipe,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule
  ],
  standalone: true
})
export class ListingsComponent implements OnInit, OnChanges, OnDestroy {
    private route = inject(ActivatedRoute);
    private serviceFactory = inject(ObjectServiceFactory);
    private clientService = inject(ClientService);
    private clientStateService = inject(ClientStateService);
    private cdRef = inject(ChangeDetectorRef);
    private toastService = inject(ToastService);
    private router = inject(Router);
    private dialog = inject(MatDialog);

  betaBackendUnavailable$ = this.clientStateService.betaBackendUnavailable$;

  @Input() selectionType?: 'single' | 'multi'; // Define expected selection types
  @Input() selectionObjectType: string;        // Define expected selection object type
  @Input() selectedItems: string[] = [];      // Default to empty array
  @Input() disallowSelectionForIds: string[] = []; // Define ids that should not be selectable
  @Input() multiselect: boolean = true;       // Determines if multiple selections are allowed

  @Output() selectionChange = new EventEmitter<string[]>(); // Emit selection changes

  items$: Observable<any[]>;
  filteredItems: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  objectType: string;
  objectService: ObjectService<any>;
  private destroy$ = new Subject<void>();
  displayedColumns: string[] = ['name', 'url', 'status', 'model', 'actions'];
  private readonly duplicableObjectTypes = new Set(['assistants', 'chatbots', 'functions']);
  
  // Search control for filtering
  searchControl = new FormControl('');

  // Initialize selection model based on selectionType
  selection: SelectionModel<string>;

    constructor() {
    // Initialize SelectionModel conditionally
    this.selection = new SelectionModel<string>(this.multiselect, this.selectedItems);
  }

  // Getter to check if selection mode is active
  get isSelectionMode(): boolean {
    return !!this.selectionType;
  }

  ngOnInit(): void {
    this.items$ = combineLatest([
      this.route.paramMap.pipe(
          map((params: ParamMap) => this.selectionObjectType ?? params.get('type')!),
          distinctUntilChanged()
      ),
      this.clientStateService.betaBackendUnavailable$
    ]).pipe(
        takeUntil(this.destroy$),
        switchMap(([type, betaBackendUnavailable]: [string, boolean]) => {
          this.objectType = type;
          this.objectService = this.serviceFactory.getService(this.objectType);
          this.updateDisplayColumns();
          if (this.objectType === 'chatbots' && betaBackendUnavailable) {
            return of([]);
          }

          return this.objectService.getAllAsync();
        }),
        map(items => {
          // Sort items alphabetically by name
          return items.sort((a, b) => {
            if (a.name && b.name) {
              return a.name.localeCompare(b.name);
            }
            return 0;
          });
        }),
        defaultIfEmpty([]),
        tap((items) => {
          this.filteredItems.data = items;
          this.cdRef.detectChanges();
        })
    );
    if (this.isSelectionMode) {
      this.syncSelectionFromInput();
    }
    
    // Setup search filtering
    this.setupSearchFilter();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedItems'] && this.isSelectionMode) {
      this.syncSelectionFromInput();
    }
  }

  private syncSelectionFromInput(): void {
    const selected = Array.from(new Set((this.selectedItems ?? []).filter(id => !!id)));
    this.selection.clear();
    this.selection.select(...selected);
  }

  reloadPage(): void {
    window.location.reload();
  }
  
  /**
   * Sets up the search filter functionality
   */
  setupSearchFilter(): void {
    // Configure filter predicate for the data source
    this.filteredItems.filterPredicate = (data: any, filter: string) => {
      const searchText = filter.toLowerCase();
      
      // Search in name
      if (data.name && data.name.toLowerCase().includes(searchText)) {
        return true;
      }
      
      // Search in description
      if (data.description && data.description.toLowerCase().includes(searchText)) {
        return true;
      }
      
      // Search in URL
      if (data.baseUrl && data.baseUrl.toLowerCase().includes(searchText)) {
        return true;
      }

      if (data.url && data.url.toLowerCase().includes(searchText)) {
        return true;
      }
      
      // Search in model
      if (data.model && data.model.toLowerCase().includes(searchText)) {
        return true;
      }
      
      // Search in type
      if (data.type && data.type.toLowerCase().includes(searchText)) {
        return true;
      }
      
      return false;
    };
    
    // Subscribe to search control value changes
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
      tap(() => this.applyFilter())
    ).subscribe();
  }
  
  /**
   * Applies the search filter to the data source
   */
  applyFilter(): void {
    const searchValue = this.searchControl.value?.trim().toLowerCase() || '';
    this.filteredItems.filter = searchValue;
  }

  updateDisplayColumns(): void {
    switch (this.objectType) {
      case 'chatbots':
        this.displayedColumns = ['name', 'url', 'model', 'status', 'actions'];
        break;
      case 'assistants':
        this.displayedColumns = ['name', 'model', 'status', 'actions'];
        break;
      case 'tasks':
        this.displayedColumns = ['name', 'model', 'status', 'schedule', 'notifications', 'actions'];
        break;
      case 'workflows':
        this.displayedColumns = ['name', 'description', 'actions'];
        break;
      case 'product-feeds':
        this.displayedColumns = ['name', 'market', 'graphRole', 'indexState', 'lastActivity', 'status', 'actions'];
        break;
      case 'functions':
        this.displayedColumns = ['name', 'type', 'status', 'actions'];
        break;
      case 'files':
        this.displayedColumns = ['name', 'description', 'filetype', 'status', 'actions'];
        break;
      default:
        this.displayedColumns = ['name', 'url', 'status', 'model', 'actions'];
    }

    // Add 'select' column if in selection mode
    if (this.isSelectionMode) {
      this.displayedColumns.unshift('select');
      this.displayedColumns.pop();
    }

    // Avoid rendering a duplicate description column when the description already appears under the name.
    if (this.displayedColumns.includes('description')) {
      this.displayedColumns = this.displayedColumns.filter(column => column !== 'description');
    }
  }

  matchFileTypeWithTablerIcons(fileName: string): string {
    const fileExtension = fileName?.split('.').pop() ?? '';
    const fileExtensionToIconMap: Record<string, string> = {
      'c': 'file-code',
      'cs': 'brand-c-sharp',
      'cpp': 'brand-cpp',
      'doc': 'file-type-doc',
      'docx': 'file-type-docx',
      'html': 'html',
      'java': 'file-code-2',
      'json': 'json',
      'md': 'markdown',
      'pdf': 'pdf',
      'php': 'brand-php',
      'pptx': 'file-type-ppt',
      'py': 'brand-python',
      'rb': 'brand-cucumber',
      'tex': 'archive',
      'txt': 'file-type-txt',
      'css': 'file-type-css',
      'js': 'file-type-js',
      'sh': 'brand-powershell',
      'ts': 'file-type-ts'
    };
    return fileExtensionToIconMap[fileExtension] || 'file';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Toggles the selection state of a single row.
   * @param row The row to toggle.
   */
  toggleSelection(row: any): void {
    if (this.isSelectionDisallowed(row)) {
      return;
    }
    this.selection.toggle(row.id);
    this.emitSelectionChange();
  }

  /**
   * Checks if all rows are selected.
   * @param rows The array of all rows.
   * @returns True if all rows are selected, false otherwise.
   */
  isAllSelected(rows: any[]): boolean {
    const eligibleRows = rows.filter(row => !this.isSelectionDisallowed(row));
    return eligibleRows.length > 0 && eligibleRows.every(row => this.selection.isSelected(row.id));
  }

  /**
   * Toggles selection of all rows.
   * @param rows The array of all rows.
   */
  masterToggle(rows: any[]): void {
    const eligibleRows = rows.filter(row => !this.isSelectionDisallowed(row));
    if (this.isAllSelected(rows)) {
      eligibleRows.forEach(row => this.selection.deselect(row.id));
    } else {
      eligibleRows.forEach(row => this.selection.select(row.id));
    }
    this.emitSelectionChange();
  }

  isSelectionDisallowed(row: any): boolean {
    if (this.selection.isSelected(row?.id)) {
      return false;
    }
    return this.disallowSelectionForIds.includes(row?.id) ||
      (this.objectType === 'files' && row?.enabled === false);
  }

  /**
   * Emits the current selection to parent components.
   */
  private emitSelectionChange(): void {
    this.selectionChange.emit(this.selection.selected);
  }

  /**
   * Opens the dialog to create a new object.
   */
  openCreateDialog(): void {
    const dialogRef = this.dialog.open(CreateObjectDialogComponent, {
      width: '400px',
      data: { objectType: this.objectType }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.createNewObject(result);
      }
    });
  }

  /**
   * Creates a new object using the ObjectService.
   * @param data The data of the new object.
   */
  createNewObject(data: any): void {

    if (this.objectType === 'functions') {
      (this.objectService as FunctionsService).createDraftAsync(data.name).subscribe({
        next: draft => {
          this.toastService.showSuccess('Function draft created, redirecting...');
          this.router.navigate([`/ai/functions/${draft.id}`]);
        },
        error: () => this.toastService.showError('We couldn’t confirm creation. Refresh the function list before trying again.')
      });
      return;
    }

    data.id = this.generateGuid();
    data.clientId = this.clientService.client?.id;

    this.objectService.saveAsync(data).subscribe({
      // Once completed, we wait to display a toast, then wait until the object exists on the database (checked by calling the api every second) and redirect to the id. No need to update the list, as the user will be redirected to the new object.
      next: () => {
          // Handle result, possibly show a notification
          this.toastService.showSuccess('Object created successfully, redirecting...');
          this.getById(data);
      },
      error: (err) => {
        // Handle error, possibly show a notification
        console.error('Error creating object:', err);
        // Optionally, show an error message to the user
      }
    });
  }

  async getById(data: any) {
    this.objectService.getByIdAsync(data.id).subscribe({
      next: () => {
        this.router.navigate([`/ai/${this.objectType}/${data.id}`]);
      },
      error: () => {
        // wait 1 second and try again
        setTimeout(() => {
          this.getById(data);
        }, 1000);
      }
    });
  }

  getValidImage(element: any): string {
    return (
        (element.image?.trim() ||
            element.widgetOptions?.images?.aiLogo1?.trim() ||
            element.frontEndOptions?.chatAvatar?.trim()) ||
        'https://www.markedspartner.no/hubfs/Logomark-Color%20(1).png'
    );
  }

  getListingThumbnailImage(element: any): string {
    return this.toListingThumbnailImage(this.getValidImage(element));
  }

  private toListingThumbnailImage(imageSource: string): string {
    const trimmedSource = imageSource.trim();

    if (!trimmedSource || this.isInlineImageSource(trimmedSource)) {
      return trimmedSource;
    }

    try {
      const url = new URL(trimmedSource);
      const normalizedPath = url.pathname.replace('/hubfs/', '/hs-fs/hubfs/');

      if (!this.isHubSpotFileUrl(url) || normalizedPath === url.pathname && !url.pathname.includes('/hs-fs/hubfs/')) {
        return trimmedSource;
      }

      url.pathname = normalizedPath;
      url.searchParams.set('width', '96');
      return url.toString();
    } catch {
      return trimmedSource;
    }
  }

  private isInlineImageSource(imageSource: string): boolean {
    return /^(data|blob):/i.test(imageSource);
  }

  private isHubSpotFileUrl(url: URL): boolean {
    return (
      url.pathname.includes('/hubfs/') ||
      url.pathname.includes('/hs-fs/hubfs/')
    );
  }

  canDuplicate(): boolean {
    return !this.isSelectionMode && this.duplicableObjectTypes.has(this.objectType);
  }

  productFeedGraphRole(element: any): string {
    if (String(element?.sourceFeedId ?? '').trim()) {
      return 'Detail feed';
    }

    return element?.isDefaultFeed ? 'Root / standalone · Default' : 'Root / standalone';
  }

  productFeedIndexState(element: any): string {
    const publishedFeed = this.productFeedPublishedFeed(element);
    if (!publishedFeed) {
      return 'Root-owned · Root unavailable';
    }

    const state = this.productFeedOwnIndexState(publishedFeed);
    return publishedFeed === element ? state : `Root-owned · ${state}`;
  }

  productFeedIndexedProductCount(element: any): number | null {
    const publishedFeed = this.productFeedPublishedFeed(element);
    const productCount = publishedFeed?.luceneSnapshot?.totalDocumentCount;
    return Number.isInteger(productCount) && productCount >= 0 ? productCount : null;
  }

  productFeedFetchDate(element: any): string | null {
    const publishedFeed = this.productFeedPublishedFeed(element);
    const value = String(publishedFeed?.lastFetchDateTime ?? '').trim();
    return value && Number.isFinite(Date.parse(value)) ? value : null;
  }

  productFeedPublicationDate(element: any): string | null {
    const publishedFeed = this.productFeedPublishedFeed(element);
    const value = String(publishedFeed?.indexGenerationPublishedAt ?? '').trim();
    return value && Number.isFinite(Date.parse(value)) ? value : null;
  }

  productFeedUsesRootPublication(element: any): boolean {
    return !!String(element?.sourceFeedId ?? '').trim();
  }

  private productFeedPublishedFeed(element: any): any | null {
    const sourceFeedId = String(element?.sourceFeedId ?? '').trim();
    if (!sourceFeedId) {
      return element ?? null;
    }

    return this.filteredItems.data.find(feed => String(feed?.id ?? '').trim() === sourceFeedId) ?? null;
  }

  private productFeedOwnIndexState(element: any): string {
    if (!String(element?.indexGenerationId ?? '').trim()) {
      return 'Not published';
    }

    const productCount = element?.luceneSnapshot?.totalDocumentCount;
    if (!Number.isInteger(productCount) || productCount < 0) {
      return 'Published';
    }

    return Number.isInteger(productCount) && productCount > 0 ? 'Ready' : 'Published empty';
  }

  duplicateObject(element: any, event: Event): void {
    event.stopPropagation();

    if (!this.canDuplicate()) {
      return;
    }

    const duplicated = this.prepareDuplicate(element);
    this.toastService.showInfo(`Duplicating ${this.singularObjectType()}...`, 3000);

    this.objectService.saveAsync(duplicated).subscribe({
      next: () => {
        this.toastService.showSuccess(`${this.singularObjectType()} duplicated. Redirecting...`, 3000);
        this.getById(duplicated);
      },
      error: (err) => {
        console.error('Error duplicating object:', err);
        this.toastService.showError(`Error duplicating ${this.singularObjectType()}.`, 3000);
      }
    });
  }

  private prepareDuplicate(source: any): any {
    const duplicated = JSON.parse(JSON.stringify(source));
    duplicated.id = this.generateGuid();
    duplicated.clientId = this.clientService.client?.id;
    duplicated.name = this.nextDuplicateName(source?.name ?? this.singularObjectType());
    duplicated.enabled = false;

    if (duplicated.scheduleSettings?.scheduleEnabled !== undefined) {
      duplicated.scheduleSettings.scheduleEnabled = false;
    }

    delete duplicated.assistantId;
    delete duplicated.vectorStoreId;
    delete duplicated.lastFetchDateTime;
    delete duplicated.updatedDate;
    delete duplicated.publishedDate;

    return duplicated;
  }

  private nextDuplicateName(name: string): string {
    const baseName = name.endsWith(' (copy)') ? name.replace(/ \(copy\)$/, '') : name;
    const existingNames = new Set(this.filteredItems.data.map(item => item?.name).filter(Boolean));
    let candidate = `${baseName} (copy)`;
    let copyNumber = 2;

    while (existingNames.has(candidate)) {
      candidate = `${baseName} (copy ${copyNumber})`;
      copyNumber++;
    }

    return candidate;
  }

  private singularObjectType(): string {
    return (this.objectType ?? 'object').replace(/s$/, '').replace('-', ' ');
  }

  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

}
